import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Science as TestIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { adminService, cepService } from "../services/api";

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Estados para dados
  const [escritorios, setEscritorios] = useState([]);
  const [stats, setStats] = useState(null);
  const [planos, setPlanos] = useState([]);
  const [stripePrices, setStripePrices] = useState([]);

  // Estados para modais
  const [escritorioModal, setEscritorioModal] = useState({
    open: false,
    editing: null,
    activeTab: 0, // 0 = Principal, 1 = Assinatura, 2 = Usuários
    data: {
      nome: "",
      fantasia: "",
      cnpj: "",
      telefone: "",
      email: "",
      natureza: "",
      logradouro: "",
      numero: "",
      complemento: "",
      cep: "",
      bairro: "",
      cidade: "",
      uf: "",
      numero_usuarios: "",
      plano_id: "",
      periodo_atual_fim: "",
      nomeResponsavel: "",
      emailResponsavel: "",
      senhaResponsavel: "",
      ativo: true,
    },
    cnpjError: null,
  });

  const [planoModal, setPlanoModal] = useState({
    open: false,
    editing: null,
    data: {
      nome: "",
      preco_centavos: "",
      intervalo: "month",
      id_gateway_plano: "",
      ativo: true,
    },
  });

  const [stripeModal, setStripeModal] = useState({
    open: false,
    secretKey: "",
    testing: false,
  });

  // Estado para controle da busca de CEP
  const [buscandoCEP, setBuscandoCEP] = useState(false);
  const [salvandoEscritorio, setSalvandoEscritorio] = useState(false);
  const [salvandoPlano, setSalvandoPlano] = useState(false);
  const [excluindoEscritorio, setExcluindoEscritorio] = useState(false);
  const [excluindoPlano, setExcluindoPlano] = useState(false);

  // Estados para confirmação de exclusão
  const [confirmacaoExclusaoEscritorio, setConfirmacaoExclusaoEscritorio] =
    useState({
      aberto: false,
      escritorioId: null,
      escritorioNome: "",
    });

  const [confirmacaoExclusaoPlano, setConfirmacaoExclusaoPlano] = useState({
    aberto: false,
    planoId: null,
    planoNome: "",
  });

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [escritoriosData, statsData, planosData] = await Promise.all([
        adminService.getEscritorios({ ativo_filter: "all" }), // AdminDashboard mostra todos
        adminService.getDashboardStats(),
        adminService.getPlanos(),
      ]);

      setEscritorios(escritoriosData);
      setStats(statsData);
      setPlanos(planosData);
    } catch (err) {
      setError(err.message || "Ocorreu um erro ao carregar os dados.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadStripePrices = async () => {
    try {
      const prices = await adminService.getStripePrices();
      setStripePrices(prices);
    } catch (err) {
      console.error("Erro ao carregar preços do Stripe:", err);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (newValue === 2) {
      // Tab Stripe
      loadStripePrices();
    }
  };

  // ==================== ESCRITÓRIOS ====================

  const openEscritorioModal = (escritorio = null) => {
    setEscritorioModal({
      open: true,
      editing: escritorio,
      activeTab: 0, // Sempre inicia na primeira aba (Principal)
      data: escritorio
        ? {
            nome: escritorio.nome,
            fantasia: escritorio.fantasia || "",
            cnpj: escritorio.cnpj || "",
            telefone: escritorio.telefone || "",
            email: escritorio.email || "",
            natureza: escritorio.natureza || "",
            logradouro: escritorio.logradouro || "",
            numero: escritorio.numero || "",
            complemento: escritorio.complemento || "",
            cep: escritorio.cep || "",
            bairro: escritorio.bairro || "",
            cidade: escritorio.cidade || "",
            uf: escritorio.uf || "",
            plano_id: escritorio.assinaturas?.[0]?.plano?.id || "",
            periodo_atual_fim: formatDateForInput(
              escritorio.assinaturas?.[0]?.periodo_atual_fim,
            ),
            nomeResponsavel:
              escritorio.usuarios?.find((u) => u.role === "admin")?.nome || "",
            emailResponsavel:
              escritorio.usuarios?.find((u) => u.role === "admin")?.email || "",
            senhaResponsavel: "",
            ativo: escritorio.ativo !== undefined ? escritorio.ativo : true,
          }
        : {
            nome: "",
            fantasia: "",
            cnpj: "",
            telefone: "",
            email: "",
            natureza: "",
            logradouro: "",
            numero: "",
            complemento: "",
            cep: "",
            bairro: "",
            cidade: "",
            uf: "",
            plano_id: "",
            periodo_atual_fim: "",
            nomeResponsavel: "",
            emailResponsavel: "",
            senhaResponsavel: "",
            ativo: true,
          },
    });
  };

  const closeEscritorioModal = () => {
    setEscritorioModal({
      open: false,
      editing: null,
      activeTab: 0, // Principal
      data: {
        nome: "",
        fantasia: "",
        cnpj: "",
        telefone: "",
        email: "",
        natureza: "",
        logradouro: "",
        numero: "",
        complemento: "",
        cep: "",
        bairro: "",
        cidade: "",
        uf: "",
        plano_id: "",
        periodo_atual_fim: "",
        nomeResponsavel: "",
        emailResponsavel: "",
        senhaResponsavel: "",
        ativo: true,
      },
    });
  };

  const handleEscritorioSave = async () => {
    if (salvandoEscritorio) return; // Evita cliques duplos

    try {
      setSalvandoEscritorio(true);
      // Validação básica
      if (!escritorioModal.data.nome || !escritorioModal.data.nome.trim()) {
        setError("Nome do escritório é obrigatório");
        return;
      }

      // Validações específicas para criação de novo escritório
      if (!escritorioModal.editing) {
        if (
          !escritorioModal.data.nomeResponsavel ||
          !escritorioModal.data.nomeResponsavel.trim()
        ) {
          setError("Nome do responsável é obrigatório");
          return;
        }
        if (
          !escritorioModal.data.emailResponsavel ||
          !escritorioModal.data.emailResponsavel.trim()
        ) {
          setError("Email do responsável é obrigatório");
          return;
        }
        if (
          !escritorioModal.data.senhaResponsavel ||
          escritorioModal.data.senhaResponsavel.length < 6
        ) {
          setError("Senha deve ter pelo menos 6 caracteres");
          return;
        }
      }

      const dados = {
        nome: escritorioModal.data.nome.trim(),
        fantasia: escritorioModal.data.fantasia,
        cnpj: escritorioModal.data.cnpj,
        telefone: escritorioModal.data.telefone,
        email: escritorioModal.data.email,
        natureza: escritorioModal.data.natureza,
        logradouro: escritorioModal.data.logradouro,
        numero: escritorioModal.data.numero,
        complemento: escritorioModal.data.complemento,
        cep: escritorioModal.data.cep,
        bairro: escritorioModal.data.bairro,
        cidade: escritorioModal.data.cidade,
        uf: escritorioModal.data.uf,
        numero_usuarios:
          escritorioModal.data.numero_usuarios === "" ||
          escritorioModal.data.numero_usuarios === null
            ? null
            : parseInt(escritorioModal.data.numero_usuarios),
        plano_id: escritorioModal.data.plano_id || null,
        periodo_atual_fim: formatDateForBackend(
          escritorioModal.data.periodo_atual_fim,
        ),
        ativo: escritorioModal.data.ativo,
      };

      // Adicionar dados do responsável apenas na criação
      if (!escritorioModal.editing) {
        dados.nomeResponsavel = escritorioModal.data.nomeResponsavel.trim();
        dados.emailResponsavel = escritorioModal.data.emailResponsavel.trim();
        dados.senhaResponsavel = escritorioModal.data.senhaResponsavel;
      }

      if (escritorioModal.editing) {
        await adminService.updateEscritorio(escritorioModal.editing.id, dados);
      } else {
        await adminService.createEscritorio(dados);
      }

      closeEscritorioModal();
      loadInitialData();
    } catch (err) {
      setError(err.message);
    } finally {
      setSalvandoEscritorio(false);
    }
  };

  const handleEscritorioDelete = (escritorio) => {
    setConfirmacaoExclusaoEscritorio({
      aberto: true,
      escritorioId: escritorio.id,
      escritorioNome: escritorio.nome,
    });
  };

  const confirmarExclusaoEscritorio = async () => {
    if (excluindoEscritorio) return; // Evita cliques duplos

    try {
      setExcluindoEscritorio(true);
      await adminService.deleteEscritorio(
        confirmacaoExclusaoEscritorio.escritorioId,
      );
      setConfirmacaoExclusaoEscritorio({
        aberto: false,
        escritorioId: null,
        escritorioNome: "",
      });
      loadInitialData();
    } catch (err) {
      setError(err.message);
    } finally {
      setExcluindoEscritorio(false);
    }
  };

  // ==================== PLANOS ====================

  const openPlanoModal = (plano = null) => {
    setPlanoModal({
      open: true,
      editing: plano,
      data: plano
        ? {
            nome: plano.nome,
            preco_centavos: plano.preco_centavos,
            intervalo: plano.intervalo,
            id_gateway_plano: plano.id_gateway_plano,
            ativo: plano.ativo,
          }
        : {
            nome: "",
            preco_centavos: "",
            intervalo: "month",
            id_gateway_plano: "",
            ativo: true,
          },
    });
  };

  const closePlanoModal = () => {
    setPlanoModal({ open: false, editing: null, data: {} });
  };

  const handlePlanoSave = async () => {
    if (salvandoPlano) return; // Evita cliques duplos

    try {
      setSalvandoPlano(true);

      const dados = {
        ...planoModal.data,
        preco_centavos: parseInt(planoModal.data.preco_centavos),
      };

      if (planoModal.editing) {
        await adminService.updatePlano(planoModal.editing.id, dados);
      } else {
        await adminService.createPlano(dados);
      }

      closePlanoModal();
      loadInitialData();
    } catch (err) {
      setError(err.message);
    } finally {
      setSalvandoPlano(false);
    }
  };

  const handlePlanoDelete = (plano) => {
    setConfirmacaoExclusaoPlano({
      aberto: true,
      planoId: plano.id,
      planoNome: plano.nome,
    });
  };

  const confirmarExclusaoPlano = async () => {
    if (excluindoPlano) return; // Evita cliques duplos

    try {
      setExcluindoPlano(true);
      await adminService.deletePlano(confirmacaoExclusaoPlano.planoId);
      setConfirmacaoExclusaoPlano({
        aberto: false,
        planoId: null,
        planoNome: "",
      });
      loadInitialData();
    } catch (err) {
      setError(err.message);
    } finally {
      setExcluindoPlano(false);
    }
  };

  // ==================== STRIPE ====================

  const openStripeModal = () => {
    setStripeModal({ open: true, secretKey: "", testing: false });
  };

  const closeStripeModal = () => {
    setStripeModal({ open: false, secretKey: "", testing: false });
  };

  const handleStripeTest = async () => {
    try {
      setStripeModal((prev) => ({ ...prev, testing: true }));
      await adminService.testStripe(stripeModal.secretKey);
      alert("Conexão com Stripe estabelecida com sucesso!");
      closeStripeModal();
    } catch (err) {
      setError(err.message);
    } finally {
      setStripeModal((prev) => ({ ...prev, testing: false }));
    }
  };

  const formatCurrency = (centavos) => {
    return (centavos / 100).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  };

  const formatDateForInput = (dateString) => {
    if (!dateString) return "";

    try {
      // Limpa a string e remove espaços
      const cleanDate = String(dateString).trim();

      // Se já está no formato YYYY-MM-DD, retorna direto
      if (/^\d{4}-\d{2}-\d{2}$/.test(cleanDate)) {
        return cleanDate;
      }

      // Se tem horário, pega só a parte da data
      if (cleanDate.includes("T")) {
        return cleanDate.split("T")[0];
      }

      // Se tem espaço (formato datetime do MySQL), pega só a data
      if (cleanDate.includes(" ")) {
        return cleanDate.split(" ")[0];
      }

      // Tenta interpretar outros formatos
      const date = new Date(cleanDate);
      if (isNaN(date.getTime())) {
        return "";
      }

      // Retorna no formato YYYY-MM-DD
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error("Erro ao formatar data para input:", error);
      return "";
    }
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "-";

    try {
      let date;

      // Se está no formato YYYY-MM-DD
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        const [year, month, day] = dateString.split("-");
        date = new Date(year, month - 1, day); // month - 1 porque Date usa 0-11
      } else {
        date = new Date(dateString);
      }

      if (isNaN(date.getTime())) {
        return "-";
      }

      return date.toLocaleDateString("pt-BR");
    } catch (error) {
      console.error("Erro ao formatar data para exibição:", error);
      return "-";
    }
  };

  const formatDateForBackend = (dateString) => {
    if (!dateString) return null;

    try {
      // Se já está no formato YYYY-MM-DD, adiciona o horário
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString + "T23:59:59.999Z";
      }

      return dateString;
    } catch (error) {
      console.error("Erro ao formatar data para backend:", error);
      return null;
    }
  };

  // Utilitário para verificar se escritório pode ser excluído
  const canDeleteEscritorio = (escritorio) => {
    if (!escritorio.usuarios) return false;
    const usuariosAtivos = escritorio.usuarios.filter((u) => u.ativo !== false);
    return usuariosAtivos.length === 0;
  };

  const getUsuariosAtivos = (escritorio) => {
    if (!escritorio.usuarios) return [];
    return escritorio.usuarios.filter((u) => u.ativo !== false);
  };

  // Função para formatar CPF/CNPJ
  const formatCPFCNPJ = (value) => {
    const numbers = value.replace(/\D/g, "");

    // Se tem 11 dígitos, formata como CPF
    if (numbers.length <= 11) {
      return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, "$1.$2.$3-$4");
    }

    // Se tem 14 dígitos, formata como CNPJ
    if (numbers.length <= 14) {
      return numbers.replace(
        /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
        "$1.$2.$3/$4-$5",
      );
    }

    return value;
  };

  // Estado para busca de CNPJ
  const [buscandoCNPJ, setBuscandoCNPJ] = useState(false);

  // Lista de UFs do Brasil
  const UF_LIST = [
    "AC",
    "AL",
    "AP",
    "AM",
    "BA",
    "CE",
    "DF",
    "ES",
    "GO",
    "MA",
    "MT",
    "MS",
    "MG",
    "PA",
    "PB",
    "PR",
    "PE",
    "PI",
    "RJ",
    "RN",
    "RS",
    "RO",
    "RR",
    "SC",
    "SP",
    "SE",
    "TO",
  ];

  // Função para buscar CNPJ
  const buscarCNPJ = async () => {
    if (
      !escritorioModal.data.cnpj ||
      escritorioModal.data.cnpj.replace(/\D/g, "").length !== 14
    ) {
      setEscritorioModal((prev) => ({
        ...prev,
        cnpjError: "Informe um CNPJ válido para buscar.",
      }));
      return;
    }
    setBuscandoCNPJ(true);
    try {
      const data = await adminService.consultaCNPJ(escritorioModal.data.cnpj);
      setEscritorioModal((prev) => ({
        ...prev,
        data: {
          ...prev.data,
          nome: data.nome || prev.data.nome,
          fantasia: data.fantasia || prev.data.fantasia,
          cnpj: data.cnpj || prev.data.cnpj,
          telefone: data.telefone || prev.data.telefone,
          email: data.email || prev.data.email,
          natureza: data.natureza || prev.data.natureza,
          logradouro: data.logradouro || prev.data.logradouro,
          numero: data.numero || prev.data.numero,
          complemento: data.complemento || prev.data.complemento,
          cep: data.cep || prev.data.cep,
          bairro: data.bairro || prev.data.bairro,
          cidade: data.cidade || prev.data.cidade,
          uf: data.uf || prev.data.uf,
        },
      }));
      setEscritorioModal((prev) => ({ ...prev, cnpjError: null }));
    } catch (error) {
      const responseData = error && error.response && error.response.data;
      const rawMessage =
        (responseData && (responseData.message || responseData.error)) ||
        (typeof responseData === "string" ? responseData : null) ||
        error?.message ||
        "";
      const lowerMsg = String(rawMessage).toLowerCase();
      let friendlyMessage = rawMessage;
      if (
        lowerMsg.includes("not in cache") ||
        lowerMsg.includes("not_found") ||
        lowerMsg.includes("not found")
      ) {
        friendlyMessage = "CNPJ inexistente na Receita Federal";
      }
      setEscritorioModal((prev) => ({
        ...prev,
        cnpjError: friendlyMessage || "Erro ao buscar CNPJ.",
      }));
    } finally {
      setBuscandoCNPJ(false);
    }
  };

  // Função para buscar CEP
  const buscarCEP = async (cep) => {
    const cepLimpo = cep.replace(/\D/g, "");

    // Só busca se tiver 8 dígitos
    if (cepLimpo.length !== 8) {
      return;
    }

    setBuscandoCEP(true);
    try {
      const data = await cepService.buscarCEP(cep);

      setEscritorioModal((prev) => ({
        ...prev,
        data: {
          ...prev.data,
          logradouro: data.logradouro || prev.data.logradouro,
          bairro: data.bairro || prev.data.bairro,
          cidade: data.localidade || prev.data.cidade,
          uf: data.uf || prev.data.uf,
        },
      }));
    } catch (error) {
      console.error("Erro ao buscar CEP:", error.message);
      // Não mostra erro para o usuário, apenas no console
    } finally {
      setBuscandoCEP(false);
    }
  };

  // Função para formatar CEP
  const formatarCEP = (value) => {
    const cepLimpo = value.replace(/\D/g, "");
    return cepLimpo.replace(/(\d{5})(\d{3})/, "$1-$2");
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Painel Administrativo
      </Typography>

      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label="Dashboard" />
        <Tab label="Escritórios" />
        <Tab label="Planos" />
        <Tab label="Stripe" />
      </Tabs>

      {/* TAB 0 - DASHBOARD */}
      {activeTab === 0 && stats && (
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 3 }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Escritórios
                </Typography>
                <Typography variant="h4">{stats.escritorios.total}</Typography>
                <Typography variant="body2">
                  {stats.escritorios.com_assinatura_ativa} com assinatura ativa
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Usuários
                </Typography>
                <Typography variant="h4">{stats.usuarios.total}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Assinaturas Ativas
                </Typography>
                <Typography variant="h4">{stats.assinaturas.ativas}</Typography>
                <Typography variant="body2">
                  {stats.assinaturas.total} total
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Receita Mensal
                </Typography>
                <Typography variant="h4">
                  R$ {stats.receita.mensal_estimada_reais}
                </Typography>
                <Typography variant="body2">Estimada</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* TAB 1 - ESCRITÓRIOS */}
      {activeTab === 1 && (
        <Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Gestão de Escritórios</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => openEscritorioModal()}
            >
              Novo Escritório
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Nome do Escritório</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Plano Atual</TableCell>
                  <TableCell>Status Assinatura</TableCell>
                  <TableCell>Data Final</TableCell>
                  <TableCell>Nº Usuários</TableCell>
                  <TableCell>Usuários contratados</TableCell>
                  <TableCell>Admin</TableCell>
                  <TableCell>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {escritorios.map((escritorio) => {
                  const admin = escritorio.usuarios.find(
                    (u) => u.role === "admin",
                  );
                  const assinatura = escritorio.assinaturas?.[0];
                  const plano = assinatura?.plano;
                  const dataFinal = assinatura?.periodo_atual_fim;
                  const isAtiva = assinatura?.status === "active";
                  const isVencida =
                    dataFinal && new Date(dataFinal + "T00:00:00") < new Date();

                  return (
                    <TableRow key={escritorio.id}>
                      <TableCell>{escritorio.id}</TableCell>
                      <TableCell>{escritorio.nome}</TableCell>
                      <TableCell>
                        <Chip
                          label={escritorio.ativo ? "Ativo" : "Inativo"}
                          color={escritorio.ativo ? "success" : "error"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {plano ? (
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {plano.nome}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {formatCurrency(plano.preco_centavos)} /{" "}
                              {plano.intervalo === "month" ? "mês" : "ano"}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Sem plano
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {assinatura ? (
                          <Chip
                            label={
                              isVencida
                                ? "Vencida"
                                : isAtiva
                                  ? "Ativa"
                                  : assinatura.status
                            }
                            color={
                              isVencida
                                ? "error"
                                : isAtiva
                                  ? "success"
                                  : "default"
                            }
                            size="small"
                          />
                        ) : (
                          <Chip
                            label="Sem assinatura"
                            color="default"
                            size="small"
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {dataFinal ? (
                          <Typography variant="body2">
                            {formatDateForDisplay(dataFinal)}
                          </Typography>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {escritorio.usuarios.length}
                      </TableCell>
                      <TableCell align="center">
                        {escritorio.numero_usuarios === null ||
                        escritorio.numero_usuarios === undefined ? (
                          <Typography variant="body2" color="text.secondary">
                            ilimitado
                          </Typography>
                        ) : (
                          escritorio.numero_usuarios
                        )}
                      </TableCell>
                      <TableCell>
                        {admin ? (
                          <Box>
                            <Typography variant="body2">
                              {admin.nome}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {admin.email}
                            </Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Não definido
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Editar">
                          <IconButton
                            size="small"
                            onClick={() => openEscritorioModal(escritorio)}
                            color="info"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip
                          title={
                            !canDeleteEscritorio(escritorio)
                              ? `Não é possível excluir escritório com ${
                                  getUsuariosAtivos(escritorio).length
                                } usuário(s) ativo(s). Desative todos os usuários primeiro.`
                              : "Excluir escritório permanentemente"
                          }
                        >
                          <IconButton
                            size="small"
                            onClick={() => handleEscritorioDelete(escritorio)}
                            disabled={!canDeleteEscritorio(escritorio)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* TAB 2 - PLANOS */}
      {activeTab === 2 && (
        <Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Gestão de Planos</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => openPlanoModal()}
            >
              Novo Plano
            </Button>
          </Box>

          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Nome</TableCell>
                  <TableCell>Preço</TableCell>
                  <TableCell>Intervalo</TableCell>
                  <TableCell>ID Gateway</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Assinaturas</TableCell>
                  <TableCell>Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {planos.map((plano) => (
                  <TableRow key={plano.id}>
                    <TableCell>{plano.nome}</TableCell>
                    <TableCell>
                      {formatCurrency(plano.preco_centavos)}
                    </TableCell>
                    <TableCell>
                      {plano.intervalo === "month" ? "Mensal" : "Anual"}
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace" }}
                      >
                        {plano.id_gateway_plano}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={plano.ativo ? "Ativo" : "Inativo"}
                        color={plano.ativo ? "success" : "default"}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {plano.assinaturas_ativas} ativas /{" "}
                      {plano.total_assinaturas} total
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          onClick={() => openPlanoModal(plano)}
                          color="info"
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Excluir">
                        <IconButton
                          size="small"
                          onClick={() => handlePlanoDelete(plano)}
                          disabled={plano.assinaturas_ativas > 0}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* TAB 3 - STRIPE */}
      {activeTab === 3 && (
        <Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
            <Typography variant="h6">Configurações do Stripe</Typography>
            <Box>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={loadStripePrices}
                sx={{ mr: 1 }}
              >
                Atualizar Preços
              </Button>
              <Button
                variant="contained"
                startIcon={<TestIcon />}
                onClick={openStripeModal}
              >
                Testar Conexão
              </Button>
            </Box>
          </Box>

          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Preços Disponíveis no Stripe
              </Typography>
              {stripePrices.length > 0 ? (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>ID</TableCell>
                        <TableCell>Nome</TableCell>
                        <TableCell>Preço</TableCell>
                        <TableCell>Recorrência</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {stripePrices.map((price) => (
                        <TableRow key={price.id}>
                          <TableCell sx={{ fontFamily: "monospace" }}>
                            {price.id}
                          </TableCell>
                          <TableCell>{price.nickname || "Sem nome"}</TableCell>
                          <TableCell>
                            {price.unit_amount
                              ? formatCurrency(price.unit_amount)
                              : "Gratuito"}
                          </TableCell>
                          <TableCell>
                            {price.recurring
                              ? `${price.recurring.interval_count} ${price.recurring.interval}`
                              : "Único"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography color="textSecondary">
                  Clique em "Atualizar Preços" para carregar os preços do Stripe
                </Typography>
              )}
            </CardContent>
          </Card>
        </Box>
      )}

      {/* MODAL DE PLANO */}
      <Dialog
        open={planoModal.open}
        onClose={closePlanoModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {planoModal.editing ? "Editar Plano" : "Novo Plano"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="Nome do Plano"
              value={planoModal.data.nome}
              onChange={(e) =>
                setPlanoModal((prev) => ({
                  ...prev,
                  data: { ...prev.data, nome: e.target.value },
                }))
              }
              fullWidth
            />
            <TextField
              label="Preço (centavos)"
              type="number"
              value={planoModal.data.preco_centavos}
              onChange={(e) =>
                setPlanoModal((prev) => ({
                  ...prev,
                  data: { ...prev.data, preco_centavos: e.target.value },
                }))
              }
              fullWidth
              helperText="Exemplo: 2990 para R$ 29,90"
            />
            <FormControl fullWidth>
              <InputLabel>Intervalo</InputLabel>
              <Select
                value={planoModal.data.intervalo}
                onChange={(e) =>
                  setPlanoModal((prev) => ({
                    ...prev,
                    data: { ...prev.data, intervalo: e.target.value },
                  }))
                }
              >
                <MenuItem value="month">Mensal</MenuItem>
                <MenuItem value="year">Anual</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="ID do Gateway (Stripe Price ID)"
              value={planoModal.data.id_gateway_plano}
              onChange={(e) =>
                setPlanoModal((prev) => ({
                  ...prev,
                  data: { ...prev.data, id_gateway_plano: e.target.value },
                }))
              }
              fullWidth
              helperText="Exemplo: price_1AbCdEfGhI2JkLmN"
            />
            <FormControlLabel
              control={
                <Switch
                  checked={planoModal.data.ativo}
                  onChange={(e) =>
                    setPlanoModal((prev) => ({
                      ...prev,
                      data: { ...prev.data, ativo: e.target.checked },
                    }))
                  }
                />
              }
              label="Plano Ativo"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closePlanoModal}>Cancelar</Button>
          <Button
            onClick={handlePlanoSave}
            variant="contained"
            disabled={salvandoPlano}
          >
            {salvandoPlano ? <CircularProgress size={20} /> : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* MODAL DE ESCRITÓRIO */}
      <Dialog
        open={escritorioModal.open}
        onClose={closeEscritorioModal}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {escritorioModal.editing ? "Editar Escritório" : "Novo Escritório"}
        </DialogTitle>

        {/* Abas - apenas na edição */}
        {escritorioModal.editing && (
          <Box sx={{ borderBottom: 1, borderColor: "divider", px: 3 }}>
            <Tabs
              value={escritorioModal.activeTab}
              onChange={(e, newValue) =>
                setEscritorioModal((prev) => ({ ...prev, activeTab: newValue }))
              }
            >
              <Tab label="Principal" />
              <Tab label="Assinatura" />
              <Tab
                label={`Usuários (${
                  getUsuariosAtivos(escritorioModal.editing).length
                })`}
              />
            </Tabs>
          </Box>
        )}

        <DialogContent>
          {/* Aba 0: Principal - Dados do Escritório */}
          {(!escritorioModal.editing || escritorioModal.activeTab === 0) && (
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
            >
              {/* --- Linha 1: CNPJ e Status --- */}
              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    label="CPF/CNPJ"
                    value={escritorioModal.data.cnpj}
                    onChange={(e) => {
                      const formattedValue = formatCPFCNPJ(e.target.value);
                      setEscritorioModal((prev) => ({
                        ...prev,
                        data: { ...prev.data, cnpj: formattedValue },
                        cnpjError: null,
                      }));
                    }}
                    error={Boolean(escritorioModal.cnpjError)}
                    helperText={escritorioModal.cnpjError || ""}
                    inputProps={{
                      maxLength: 18, // CPF: XXX.XXX.XXX-XX (14) ou CNPJ: XX.XXX.XXX/XXXX-XX (18)
                    }}
                    InputProps={{
                      endAdornment: escritorioModal.data.cnpj &&
                        escritorioModal.data.cnpj.replace(/\D/g, "").length ===
                          14 && (
                          <InputAdornment position="end">
                            <Button
                              onClick={buscarCNPJ}
                              disabled={buscandoCNPJ}
                              size="small"
                              variant="outlined"
                            >
                              {buscandoCNPJ ? (
                                <CircularProgress size={18} />
                              ) : (
                                "Buscar CNPJ"
                              )}
                            </Button>
                          </InputAdornment>
                        ),
                    }}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={escritorioModal.data.ativo}
                      label="Status"
                      onChange={(e) =>
                        setEscritorioModal((prev) => ({
                          ...prev,
                          data: { ...prev.data, ativo: e.target.value },
                        }))
                      }
                    >
                      <MenuItem value={true}>Ativo</MenuItem>
                      <MenuItem value={false}>Inativo</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              {/* --- Linha 2: Nome e Nome Fantasia --- */}
              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    label="Nome *"
                    value={escritorioModal.data.nome || ""}
                    onChange={(e) =>
                      setEscritorioModal((prev) => ({
                        ...prev,
                        data: { ...prev.data, nome: e.target.value },
                      }))
                    }
                    required
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    label="Nome fantasia"
                    value={escritorioModal.data.fantasia || ""}
                    onChange={(e) =>
                      setEscritorioModal((prev) => ({
                        ...prev,
                        data: { ...prev.data, fantasia: e.target.value },
                      }))
                    }
                  />
                </Box>
              </Box>

              {/* --- Linha 3: Email e Telefone --- */}
              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={escritorioModal.data.email || ""}
                    onChange={(e) =>
                      setEscritorioModal((prev) => ({
                        ...prev,
                        data: { ...prev.data, email: e.target.value },
                      }))
                    }
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    label="Telefone"
                    value={escritorioModal.data.telefone || ""}
                    onChange={(e) =>
                      setEscritorioModal((prev) => ({
                        ...prev,
                        data: { ...prev.data, telefone: e.target.value },
                      }))
                    }
                  />
                </Box>
              </Box>

              {/* --- Linha 4: Natureza --- */}
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="Natureza"
                  value={escritorioModal.data.natureza || ""}
                  onChange={(e) =>
                    setEscritorioModal((prev) => ({
                      ...prev,
                      data: { ...prev.data, natureza: e.target.value },
                    }))
                  }
                />
              </Box>

              {/* --- Linha 5: Endereço (CEP, Logradouro, Número) --- */}
              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    label="CEP"
                    value={escritorioModal.data.cep || ""}
                    onChange={(e) => {
                      const valorFormatado = formatarCEP(e.target.value);
                      setEscritorioModal((prev) => ({
                        ...prev,
                        data: { ...prev.data, cep: valorFormatado },
                      }));

                      // Busca CEP automaticamente quando tiver 8 dígitos
                      const cepLimpo = e.target.value.replace(/\D/g, "");
                      if (cepLimpo.length === 8) {
                        buscarCEP(valorFormatado);
                      }
                    }}
                    InputProps={{
                      endAdornment: buscandoCEP ? (
                        <CircularProgress size={20} />
                      ) : null,
                    }}
                    placeholder="00000-000"
                    inputProps={{
                      maxLength: 9, // 8 dígitos + 1 hífen
                    }}
                  />
                </Box>
                <Box sx={{ flex: 2 }}>
                  <TextField
                    fullWidth
                    label="Logradouro"
                    value={escritorioModal.data.logradouro || ""}
                    onChange={(e) =>
                      setEscritorioModal((prev) => ({
                        ...prev,
                        data: { ...prev.data, logradouro: e.target.value },
                      }))
                    }
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    label="Número"
                    value={escritorioModal.data.numero || ""}
                    onChange={(e) =>
                      setEscritorioModal((prev) => ({
                        ...prev,
                        data: { ...prev.data, numero: e.target.value },
                      }))
                    }
                  />
                </Box>
              </Box>

              {/* --- Linha 6: Endereço (Bairro, Cidade, UF) --- */}
              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    label="Bairro"
                    value={escritorioModal.data.bairro || ""}
                    onChange={(e) =>
                      setEscritorioModal((prev) => ({
                        ...prev,
                        data: { ...prev.data, bairro: e.target.value },
                      }))
                    }
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    label="Cidade"
                    value={escritorioModal.data.cidade || ""}
                    onChange={(e) =>
                      setEscritorioModal((prev) => ({
                        ...prev,
                        data: { ...prev.data, cidade: e.target.value },
                      }))
                    }
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <FormControl fullWidth>
                    <InputLabel>UF</InputLabel>
                    <Select
                      value={escritorioModal.data.uf || ""}
                      label="UF"
                      onChange={(e) =>
                        setEscritorioModal((prev) => ({
                          ...prev,
                          data: { ...prev.data, uf: e.target.value },
                        }))
                      }
                      MenuProps={{ PaperProps: { style: { minWidth: 100 } } }}
                    >
                      {UF_LIST.map((uf) => (
                        <MenuItem key={uf} value={uf}>
                          {uf}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Box>

              {/* --- Linha 7: Complemento --- */}
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="Complemento"
                  value={escritorioModal.data.complemento || ""}
                  onChange={(e) =>
                    setEscritorioModal((prev) => ({
                      ...prev,
                      data: { ...prev.data, complemento: e.target.value },
                    }))
                  }
                />
              </Box>

              {/* Limite de usuários contratados - apenas na criação */}
              {!escritorioModal.editing && (
                <>
                  <Divider sx={{ my: 2 }}>
                    <Chip label="Limite de usuários" size="small" />
                  </Divider>
                  <TextField
                    label="Número de usuários contratados"
                    type="number"
                    inputProps={{ min: 0 }}
                    value={escritorioModal.data.numero_usuarios || ""}
                    onChange={(e) =>
                      setEscritorioModal((prev) => ({
                        ...prev,
                        data: {
                          ...prev.data,
                          numero_usuarios:
                            e.target.value === ""
                              ? ""
                              : Math.max(0, parseInt(e.target.value || 0)),
                        },
                      }))
                    }
                    fullWidth
                    helperText="Deixe vazio para ilimitado"
                  />
                </>
              )}

              {/* Campos do responsável - apenas na criação */}
              {!escritorioModal.editing && (
                <>
                  <Divider sx={{ my: 2 }}>
                    <Chip label="Dados do Responsável" size="small" />
                  </Divider>

                  <TextField
                    label="Nome do Responsável"
                    value={escritorioModal.data.nomeResponsavel || ""}
                    onChange={(e) =>
                      setEscritorioModal((prev) => ({
                        ...prev,
                        data: { ...prev.data, nomeResponsavel: e.target.value },
                      }))
                    }
                    fullWidth
                    required
                    helperText="Será o administrador inicial do escritório"
                  />

                  <TextField
                    label="Email do Responsável"
                    type="email"
                    value={escritorioModal.data.emailResponsavel || ""}
                    onChange={(e) =>
                      setEscritorioModal((prev) => ({
                        ...prev,
                        data: {
                          ...prev.data,
                          emailResponsavel: e.target.value,
                        },
                      }))
                    }
                    fullWidth
                    required
                    helperText="Email para login no sistema"
                  />

                  <TextField
                    label="Senha do Responsável"
                    type="password"
                    value={escritorioModal.data.senhaResponsavel || ""}
                    onChange={(e) =>
                      setEscritorioModal((prev) => ({
                        ...prev,
                        data: {
                          ...prev.data,
                          senhaResponsavel: e.target.value,
                        },
                      }))
                    }
                    fullWidth
                    required
                    helperText="Mínimo de 6 caracteres"
                  />
                </>
              )}
            </Box>
          )}

          {/* Aba 1: Assinatura */}
          {escritorioModal.activeTab === 1 && (
            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}
            >
              <Divider sx={{ my: 2 }}>
                <Chip label="Dados da Assinatura" size="small" />
              </Divider>

              {/* Campo numero_usuarios na aba Assinatura (edição) */}
              {escritorioModal.editing && (
                <TextField
                  label="Número de usuários contratados"
                  type="number"
                  inputProps={{ min: 0 }}
                  value={
                    escritorioModal.data.numero_usuarios ??
                    escritorioModal.editing.numero_usuarios ??
                    ""
                  }
                  onChange={(e) =>
                    setEscritorioModal((prev) => ({
                      ...prev,
                      data: {
                        ...prev.data,
                        numero_usuarios:
                          e.target.value === ""
                            ? ""
                            : Math.max(0, parseInt(e.target.value || 0)),
                      },
                    }))
                  }
                  helperText="Deixe vazio para ilimitado"
                  fullWidth
                />
              )}

              <FormControl fullWidth>
                <InputLabel>Plano</InputLabel>
                <Select
                  value={escritorioModal.data.plano_id || ""}
                  onChange={(e) =>
                    setEscritorioModal((prev) => ({
                      ...prev,
                      data: { ...prev.data, plano_id: e.target.value },
                    }))
                  }
                >
                  <MenuItem value="">Sem plano</MenuItem>
                  {planos
                    .filter((plano) => plano.ativo)
                    .map((plano) => (
                      <MenuItem key={plano.id} value={plano.id}>
                        {plano.nome} - {formatCurrency(plano.preco_centavos)} /{" "}
                        {plano.intervalo === "month" ? "mês" : "ano"}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              {escritorioModal.data.plano_id && (
                <TextField
                  label="Data Final da Assinatura"
                  type="date"
                  value={escritorioModal.data.periodo_atual_fim || ""}
                  onChange={(e) =>
                    setEscritorioModal((prev) => ({
                      ...prev,
                      data: { ...prev.data, periodo_atual_fim: e.target.value },
                    }))
                  }
                  fullWidth
                  InputLabelProps={{
                    shrink: true,
                  }}
                  helperText="Deixe vazio para sem data de expiração"
                />
              )}
            </Box>
          )}

          {/* Aba 2: Usuários do Escritório (apenas na edição) */}
          {escritorioModal.editing && escritorioModal.activeTab === 2 && (
            <Box sx={{ mt: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nome</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Perfil</TableCell>
                    <TableCell align="center">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {escritorioModal.editing.usuarios?.map((usuario) => (
                    <TableRow key={usuario.id}>
                      <TableCell>{usuario.nome}</TableCell>
                      <TableCell>{usuario.email}</TableCell>
                      <TableCell>
                        <Chip
                          label={
                            usuario.role === "admin"
                              ? "Administrador"
                              : "Padrão"
                          }
                          size="small"
                          color={
                            usuario.role === "admin" ? "primary" : "default"
                          }
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={usuario.ativo !== false ? "Ativo" : "Inativo"}
                          size="small"
                          color={
                            usuario.ativo !== false ? "success" : "default"
                          }
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {(!escritorioModal.editing.usuarios ||
                escritorioModal.editing.usuarios.length === 0) && (
                <Box
                  sx={{ textAlign: "center", py: 4, color: "text.secondary" }}
                >
                  Nenhum usuário encontrado
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEscritorioModal}>
            {escritorioModal.editing && escritorioModal.activeTab === 1
              ? "Fechar"
              : "Cancelar"}
          </Button>

          {/* Botão de salvar só aparece na aba de dados */}
          {(!escritorioModal.editing || escritorioModal.activeTab === 0) && (
            <Button
              onClick={handleEscritorioSave}
              variant="contained"
              disabled={
                salvandoEscritorio ||
                !escritorioModal.data.nome ||
                !escritorioModal.data.nome.trim() ||
                (!escritorioModal.editing &&
                  (!escritorioModal.data.nomeResponsavel ||
                    !escritorioModal.data.nomeResponsavel.trim() ||
                    !escritorioModal.data.emailResponsavel ||
                    !escritorioModal.data.emailResponsavel.trim() ||
                    !escritorioModal.data.senhaResponsavel ||
                    escritorioModal.data.senhaResponsavel.length < 6))
              }
            >
              {salvandoEscritorio ? <CircularProgress size={20} /> : "Salvar"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* MODAL DE TESTE STRIPE */}
      <Dialog
        open={stripeModal.open}
        onClose={closeStripeModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Testar Conexão com Stripe</DialogTitle>
        <DialogContent>
          <TextField
            label="Chave Secreta do Stripe"
            type="password"
            value={stripeModal.secretKey}
            onChange={(e) =>
              setStripeModal((prev) => ({ ...prev, secretKey: e.target.value }))
            }
            fullWidth
            sx={{ mt: 1 }}
            helperText="Cole aqui sua chave secreta do Stripe (sk_test_... ou sk_live_...)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeStripeModal}>Cancelar</Button>
          <Button
            onClick={handleStripeTest}
            variant="contained"
            disabled={stripeModal.testing || !stripeModal.secretKey}
          >
            {stripeModal.testing ? <CircularProgress size={20} /> : "Testar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de confirmação de exclusão de escritório */}
      <Dialog
        open={confirmacaoExclusaoEscritorio.aberto}
        onClose={() =>
          setConfirmacaoExclusaoEscritorio({
            aberto: false,
            escritorioId: null,
            escritorioNome: "",
          })
        }
      >
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir permanentemente o escritório "
            {confirmacaoExclusaoEscritorio.escritorioNome}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            ⚠️ ATENÇÃO: Esta ação irá excluir todos os dados relacionados ao
            escritório e não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setConfirmacaoExclusaoEscritorio({
                aberto: false,
                escritorioId: null,
                escritorioNome: "",
              })
            }
            color="primary"
          >
            Cancelar
          </Button>
          <Button
            onClick={confirmarExclusaoEscritorio}
            color="error"
            variant="contained"
            disabled={excluindoEscritorio}
          >
            {excluindoEscritorio ? <CircularProgress size={20} /> : "Excluir"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de confirmação de exclusão de plano */}
      <Dialog
        open={confirmacaoExclusaoPlano.aberto}
        onClose={() =>
          setConfirmacaoExclusaoPlano({
            aberto: false,
            planoId: null,
            planoNome: "",
          })
        }
      >
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <Typography>
            Tem certeza que deseja excluir o plano "
            {confirmacaoExclusaoPlano.planoNome}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Esta ação não pode ser desfeita.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setConfirmacaoExclusaoPlano({
                aberto: false,
                planoId: null,
                planoNome: "",
              })
            }
            color="primary"
          >
            Cancelar
          </Button>
          <Button
            onClick={confirmarExclusaoPlano}
            color="error"
            variant="contained"
            disabled={excluindoPlano}
          >
            {excluindoPlano ? <CircularProgress size={20} /> : "Excluir"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminDashboard;
